import { fetchAuthSession } from '@aws-amplify/core';
import { Divider, withAuthenticator } from '@aws-amplify/ui-react';
import {
	AdsClickOutlined,
	CampaignOutlined,
	ConnectWithoutContactOutlined,
	ContactPhoneOutlined,
	DarkModeOutlined,
	DomainOutlined,
	DoNotTouchOutlined,
	EmailOutlined,
	GroupOutlined,
	HelpOutlined,
	Inventory2Outlined,
	LeakAddOutlined,
	LightModeOutlined,
	LogoutOutlined,
	MenuOutlined,
	MonetizationOnOutlined,
	PeopleOutlined,
	PhoneEnabledOutlined,
	RecentActorsOutlined,
	VerifiedOutlined,
} from '@mui/icons-material';
import {
	AppBar,
	Avatar,
	Box,
	Button,
	CssBaseline,
	Drawer,
	IconButton,
	List,
	ListItemIcon,
	ListSubheader,
	Menu,
	MenuItem,
	Toolbar,
	Typography,
} from '@mui/material';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Outlet } from 'react-router-dom';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import { components, formFields } from './cognito-authentication';
import { NavGroup, NavLink, NavSubItem } from './components/NavItem';
import { InContact } from './contexts/InContact';
import { UserContext, loadContext } from './contexts/UserContext';
import { findRouteConfig, getFirstAllowedPage, hasPermission } from './routeConfig';
import { actionTwoButtonStyle } from './theme';

function MainLayout({ signOut, themeMode, setThemeMode }) {
	const [userContext, setUserContext] = useState({
		tenant: '',
		tenantRole: '',
		company: '',
		name: '',
		apiKey: '',
		licenses: [],
		customFields: [],
		queryFields: [],
		rights: [],
		telephonyProvider: '',
		securityOptions: {
			contacts: 0,
			inventoryManagement: 0,
			campaigns: 0,
			communicationProfiles: 0,
			digitalChannels: 0,
			suppression: 0,
			liveAgentSelect: 0,
			connection: 0,
			userManagement: 0,
			auditLog: 0,
			callerId: 0,
		},
	});

	const [userBtn, setUserBtn] = useState(null);
	const navigate = useNavigate();
	const location = useLocation();
	const [contactsOpen, setContactsOpen] = useState(false);
	const [managementOpen, setManagementOpen] = useState(false);
	const [suppressionOpen, setSuppressionOpen] = useState(false);
	const [callerIdOpen, setCallerIdOpen] = useState(false);
	const [omniOpen, setOmniOpen] = useState(false);
	const [connectionOpen, setConnectionOpen] = useState(false);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [miniDrawer, setMiniDrawer] = useState(false);
	const [screenSize, setScreenSize] = useState({
		width: window.innerWidth,
		height: window.innerHeight,
	});

	const drawerVariant = screenSize.width < 768 ? 'temporary' : 'permanent';
	const drawerWidth = miniDrawer ? 75 : 315;

	// --- Responsive resize ---
	useEffect(() => {
		const handleResize = () => {
			setScreenSize({ width: window.innerWidth, height: window.innerHeight });
		};
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	// --- Set page title and sidebar open state from route config ---
	useEffect(() => {
		const route = findRouteConfig(location.pathname);
		if (!route) return;

		document.title = route.title;

		const groupSetters = {
			contacts: setContactsOpen,
			management: setManagementOpen,
			omni: setOmniOpen,
			suppression: setSuppressionOpen,
			connection: setConnectionOpen,
			callerId: setCallerIdOpen,
		};
		if (route.group && groupSetters[route.group]) {
			groupSetters[route.group](true);
		}
	}, [location.pathname]);

	// --- Load user session and enforce permissions ---
	useEffect(() => {
		async function getData() {
			try {
				const session = await fetchAuthSession({ forceRefresh: true });
				const idToken = session.tokens?.idToken;
				const tenantId = idToken.payload['custom:tenant'];
				const { tenant, queryFields } = await loadContext(tenantId);
				const rights = idToken.payload['custom:securityRights'].split('|');
				const options = idToken.payload['custom:securityOptions']?.split('|');
				const approver = idToken.payload['custom:approver'];

				const securityOptions = {
					contacts: +options[0],
					inventoryManagement: +options[1],
					campaigns: +options[2],
					communicationProfiles: +options[3],
					digitalChannels: +options[4],
					suppression: +options[5],
					liveAgentSelect: +options[6],
					connection: +options[7],
					userManagement: +options[8],
					auditLog: +options[9],
					callerId: +options[10] || 0,
				};

				let context = {
					id: idToken.payload['sub'],
					tenantId: idToken.payload['custom:tenant'],
					tenantRole: idToken.payload['custom:tenantRole'],
					company: idToken.payload['custom:company'],
					name: idToken.payload['name'],
					email: idToken.payload['email'],
					apiKey: tenant.apiKey,
					customFields: tenant.customFields,
					queryFields: queryFields,
					licenses: tenant.licenses,
					rights: rights,
					telephonyProvider: tenant.telephonyProvider,
					skillLabel: '',
					dispositionLabel: '',
					country: tenant.country,
					securityOptions,
				};

				switch (context.telephonyProvider) {
					case 'NiC':
						InContact.icSettings = tenant;
						context.skillLabel = 'Skill';
						context.dispositionLabel = 'Disposition';
						break;
					case 'Webex':
						context.skillLabel = 'Queue';
						context.dispositionLabel = 'Wrap Up Code';
						break;
					case 'Genesys':
						context.skillLabel = 'Queue';
						context.dispositionLabel = 'Wrap Up';
						break;
				}

				setUserContext(context);

				// Route the user to the correct page based on permissions
				if (shouldNavigateToApprover(securityOptions, approver)) {
					navigate('/approver');
				} else if (!hasPermission(location.pathname, securityOptions)) {
					const fallback = getFirstAllowedPage(securityOptions);
					if (fallback) navigate(fallback);
				}
			} catch (error) {
				console.error(error);
			}
		}
		getData();
	}, []);

	const shouldNavigateToApprover = (options, approver) => {
		return (
			options.contacts === 0 &&
			options.inventoryManagement === 0 &&
			options.campaigns === 0 &&
			options.communicationProfiles === 0 &&
			options.digitalChannels === 0 &&
			options.suppression === 0 &&
			options.liveAgentSelect === 1 &&
			options.connection === 0 &&
			options.userManagement === 0 &&
			approver === '1'
		);
	};

	// --- Memoized sidebar section visibility ---
	const opts = userContext.securityOptions;

	const showInventory = useMemo(
		() => opts.contacts > 0 || opts.inventoryManagement > 0,
		[opts.contacts, opts.inventoryManagement]
	);
	const showEngagement = useMemo(
		() =>
			opts.callerId > 0 ||
			opts.campaigns > 0 ||
			opts.communicationProfiles > 0 ||
			opts.digitalChannels > 0 ||
			opts.suppression > 0 ||
			opts.liveAgentSelect > 0,
		[opts.callerId, opts.campaigns, opts.communicationProfiles, opts.digitalChannels, opts.suppression, opts.liveAgentSelect]
	);
	const showContactCenter = useMemo(() => opts.connection > 0, [opts.connection]);
	const showAdmin = useMemo(() => opts.userManagement > 0, [opts.userManagement]);

	// --- Avatar helpers ---
	function stringToColor(string) {
		let hash = 0;
		for (let i = 0; i < string.length; i += 1) {
			hash = string.charCodeAt(i) + ((hash << 5) - hash);
		}
		let color = '#';
		for (let i = 0; i < 3; i += 1) {
			const value = (hash >> (i * 8)) & 0xff;
			color += `00${value.toString(16)}`.slice(-2);
		}
		return color;
	}

	function stringAvatar(name, size) {
		const parts = name.split(' ');
		const initials = parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : name[0];
		return {
			sx: {
				bgcolor: themeMode === 'light' ? stringToColor(name) : 'primary.main',
				width: size,
				height: size,
				fontSize: size * 0.5,
			},
			children: initials,
		};
	}

	// --- Event handlers ---
	const handleMenuClick = useCallback(() => {
		if (drawerVariant === 'temporary') {
			setDrawerOpen((prev) => !prev);
		} else {
			setMiniDrawer((prev) => !prev);
		}
	}, [drawerVariant]);

	const handleOpenUserMenu = (event) => setUserBtn(event.currentTarget);
	const handleCloseUser = () => setUserBtn(null);
	const handleContactUs = () => { window.location.href = 'mailto:support@customerDynamics.com'; };
	const openHelp = () => { window.open('https://stonly.com/guide/en/velocity-user-manual-P8Lmpcz7Jg/Steps/3653857'); };
	const handleSignOut = () => { setUserBtn(null); signOut(); navigate('/'); };

	return (
		<Box sx={{ display: 'flex' }}>
			<CssBaseline />
			<AppBar
				position="fixed"
				elevation={0}
				sx={
					drawerVariant === 'permanent'
						? { width: `calc(100% - ${drawerWidth}px)`, bgcolor: 'background.default' }
						: { width: '100%', bgcolor: 'background.default' }
				}
			>
				<Toolbar>
					{drawerVariant === 'temporary' && (
						<Box sx={{ display: 'block', padding: '8px' }}>
							<a href="/">
								<img src="/logo.svg" alt="Velocity Logo" style={{ width: '24px', height: 'auto' }} />
							</a>
						</Box>
					)}
					<IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleMenuClick} sx={{ mr: 2 }}>
						<MenuOutlined color='text' />
					</IconButton>
					<Box display="flex" justifyContent='center' alignItems='center' gap={1} sx={{ marginLeft: 'auto' }}>
						{themeMode === 'light' && (
							<IconButton onClick={() => { localStorage.setItem('previouslySelectedTheme', 'dark'); setThemeMode('dark'); }}>
								<DarkModeOutlined color='primary' />
							</IconButton>
						)}
						{themeMode === 'dark' && (
							<IconButton onClick={() => { localStorage.setItem('previouslySelectedTheme', 'light'); setThemeMode('light'); }}>
								<LightModeOutlined color='primary' />
							</IconButton>
						)}
						{userContext.name && (
							<IconButton onClick={handleOpenUserMenu} color="inherit">
								<Avatar {...stringAvatar(userContext.name, 35)} />
							</IconButton>
						)}
					</Box>
					<Menu id="userMenu" anchorEl={userBtn} keepMounted open={!!userBtn} onClose={handleCloseUser}>
						<Box sx={{ padding: '24px' }} display="flex" flexDirection="column" gap={1}>
							{userContext.name && (
								<Box display="flex" justifyContent="center" alignItems="center" gap={2}>
									<Avatar sx={{ height: '25px', width: '25px' }} {...stringAvatar(userContext.name, 65)} />
									<Box display="flex" flexDirection="column">
										<Typography variant="h6">{userContext.name}</Typography>
										<Typography variant="body2">{userContext.company}</Typography>
									</Box>
								</Box>
							)}
							<Divider />
							<MenuItem onClick={handleContactUs}>
								<ListItemIcon><EmailOutlined /></ListItemIcon>
								Contact support
							</MenuItem>
							<MenuItem onClick={openHelp}>
								<ListItemIcon><HelpOutlined /></ListItemIcon>
								Help documentation
							</MenuItem>
							<Button onClick={handleSignOut} startIcon={<LogoutOutlined />} sx={actionTwoButtonStyle}>
								Logout
							</Button>
						</Box>
					</Menu>
				</Toolbar>
			</AppBar>
			<Drawer
				sx={{
					width: drawerWidth,
					flexShrink: 0,
					'& .MuiDrawer-paper': {
						width: drawerWidth,
						boxSizing: 'border-box',
						backgroundColor: 'background.default',
					},
				}}
				variant={drawerVariant}
				anchor="left"
				open={drawerOpen}
				onClose={() => setDrawerOpen(false)}
			>
				{!miniDrawer && (
					<Box sx={{ display: 'block', margin: '24px 24px 0 24px' }}>
						<a href="/">
							<img src="/velocity_horizontal.svg" alt="Velocity Logo" style={{ width: '100%', height: 'auto' }} />
						</a>
					</Box>
				)}
				{miniDrawer && (
					<Box sx={{ display: 'block', margin: '12px 12px 0 12px' }}>
						<a href="/">
							<img src="/logo.svg" alt="Velocity Logo" style={{ width: '100%', height: 'auto' }} />
						</a>
					</Box>
				)}
				<SimpleBar style={{ maxHeight: 'calc(100vh - 120px)' }}>
					<Box sx={{ overflow: 'auto', padding: miniDrawer ? '12px' : '24px' }}>
						<List>
							{/* INVENTORY SECTION */}
							{showInventory && (
								<ListSubheader sx={{ fontWeight: 700 }}>
									{miniDrawer ? '...' : 'INVENTORY'}
								</ListSubheader>
							)}

							{opts.contacts > 0 && (
								<NavGroup
									label="Contacts"
									icon={PeopleOutlined}
									isOpen={contactsOpen}
									onToggle={() => setContactsOpen(!contactsOpen)}
									miniDrawer={miniDrawer}
								>
									<NavSubItem label="Contact Inventory" to="/contacts" isActive={location.pathname === '/contacts'} miniDrawer={miniDrawer} />
									<NavSubItem label="Custom Fields" to="/custom-fields" isActive={location.pathname === '/custom-fields'} miniDrawer={miniDrawer} />
								</NavGroup>
							)}

							{opts.inventoryManagement > 0 && (
								<NavGroup
									label="Management"
									icon={Inventory2Outlined}
									isOpen={managementOpen}
									onToggle={() => setManagementOpen(!managementOpen)}
									miniDrawer={miniDrawer}
								>
									{opts.inventoryManagement > 1 && (
										<NavSubItem label="Contact Import" to="/contact-import" isActive={location.pathname === '/contact-import'} miniDrawer={miniDrawer} />
									)}
									{opts.inventoryManagement > 1 && (
										<NavSubItem label="Bulk Management" to="/bulk-edit" isActive={location.pathname === '/bulk-edit'} miniDrawer={miniDrawer} />
									)}
									<NavSubItem label="Contact Segmentation" to="/segments" isActive={location.pathname === '/segments'} miniDrawer={miniDrawer} />
									{opts.inventoryManagement > 1 && (
										<NavSubItem label="Field Mappings" to="/field-mappings" isActive={location.pathname === '/field-mappings'} miniDrawer={miniDrawer} />
									)}
								</NavGroup>
							)}

							{/* CUSTOMER ENGAGEMENT SECTION */}
							{showEngagement && (
								<ListSubheader sx={{ fontWeight: 700 }}>
									{miniDrawer ? '...' : 'CUSTOMER ENGAGEMENT'}
								</ListSubheader>
							)}

							{opts.callerId > 0 && (
								<NavGroup
									label="Caller ID"
									icon={ContactPhoneOutlined}
									isOpen={callerIdOpen}
									onToggle={() => setCallerIdOpen(!callerIdOpen)}
									miniDrawer={miniDrawer}
								>
									<NavSubItem label="Number Management" to="/caller-id/numbers" isActive={location.pathname === '/caller-id/numbers'} miniDrawer={miniDrawer} />
									<NavSubItem label="Configurations" to="/caller-id/configurations" isActive={location.pathname === '/caller-id/configurations'} miniDrawer={miniDrawer} />
								</NavGroup>
							)}

							{opts.campaigns > 0 && (
								<NavLink label="Campaigns" icon={CampaignOutlined} to="/campaigns" isActive={location.pathname === '/campaigns'} miniDrawer={miniDrawer} />
							)}

							{opts.communicationProfiles > 0 && (
								<Fragment>
									<NavLink label="Communication Profiles" icon={RecentActorsOutlined} to="/profiles" isActive={location.pathname === '/profiles'} miniDrawer={miniDrawer} />
									<NavLink label="Manual Dialing" icon={PhoneEnabledOutlined} to="/manual-profiles" isActive={location.pathname.includes('/manual-profiles')} miniDrawer={miniDrawer} />
								</Fragment>
							)}

							{opts.digitalChannels > 0 && (
								<NavGroup
									label="Digital Channels"
									icon={ConnectWithoutContactOutlined}
									isOpen={omniOpen}
									onToggle={() => setOmniOpen(!omniOpen)}
									miniDrawer={miniDrawer}
								>
									<NavSubItem label="Message Templates" to="/message-templates" isActive={location.pathname === '/message-templates'} miniDrawer={miniDrawer} />
									<NavSubItem label="Digital Profiles" to="/digital-profiles" isActive={location.pathname === '/digital-profiles'} miniDrawer={miniDrawer} />
								</NavGroup>
							)}

							{opts.suppression > 0 && (
								<NavGroup
									label="Suppression"
									icon={DoNotTouchOutlined}
									isOpen={suppressionOpen}
									onToggle={() => setSuppressionOpen(!suppressionOpen)}
									miniDrawer={miniDrawer}
								>
									<NavSubItem label="Suppression Profiles" to="/suppression" isActive={location.pathname === '/suppression'} miniDrawer={miniDrawer} />
									<NavSubItem label="DNC" to="/dnc-lists" isActive={location.pathname === '/dnc-lists'} miniDrawer={miniDrawer} />
									<NavSubItem label="Contact Data Suppression" to="/contact-field-suppression" isActive={location.pathname === '/contact-field-suppression'} miniDrawer={miniDrawer} />
								</NavGroup>
							)}

							{userContext.telephonyProvider === 'NiC' && opts.liveAgentSelect > 0 && (
								<NavLink label="Live Agent Select" icon={AdsClickOutlined} to="/approver" isActive={location.pathname === '/approver'} miniDrawer={miniDrawer} />
							)}

							{/* CONTACT CENTER SECTION */}
							{showContactCenter && (
								<ListSubheader sx={{ fontWeight: 700 }}>
									{miniDrawer ? '...' : 'CONTACT CENTER'}
								</ListSubheader>
							)}

							{opts.connection > 0 && (
								<NavGroup
									label="Connection"
									icon={LeakAddOutlined}
									isOpen={connectionOpen}
									onToggle={() => setConnectionOpen(!connectionOpen)}
									miniDrawer={miniDrawer}
								>
									<NavSubItem label="Connection Details" to="/connection-details" isActive={location.pathname === '/connection-details'} miniDrawer={miniDrawer} />
									<NavSubItem label="Disposition Mappings" to="/disposition-mappings" isActive={location.pathname === '/disposition-mappings'} miniDrawer={miniDrawer} />
								</NavGroup>
							)}

							{/* ADMINISTRATION SECTION */}
							{showAdmin && (
								<ListSubheader sx={{ fontWeight: 700 }}>
									{miniDrawer ? '...' : 'ADMINISTRATION'}
								</ListSubheader>
							)}

							{opts.userManagement > 0 && (
								<NavLink label="Users" icon={GroupOutlined} to="/users" isActive={location.pathname === '/users'} miniDrawer={miniDrawer} />
							)}

							{userContext.tenantRole === 'SuperAdmin' && (
								<Fragment>
									<NavLink label="Licensing" icon={MonetizationOnOutlined} to="/licensing" isActive={location.pathname.includes('/licensing')} miniDrawer={miniDrawer} />
									<NavLink label="Tenants" icon={DomainOutlined} to="/tenants" isActive={location.pathname.includes('/tenants')} miniDrawer={miniDrawer} />
								</Fragment>
							)}

							{opts.auditLog > 0 && (
								<NavLink label="Audit Log" icon={VerifiedOutlined} to="/audit-log" isActive={location.pathname.includes('/audit-log')} miniDrawer={miniDrawer} />
							)}
						</List>
					</Box>
				</SimpleBar>
			</Drawer>

			<Box component="main" sx={{ flexGrow: 1, p: 3, marginTop: '64px' }}>
				<UserContext.Provider value={userContext}>
					<Outlet />
				</UserContext.Provider>
			</Box>
		</Box>
	);
}

export const AuthenticatedLayout = withAuthenticator(MainLayout, {
	hideSignUp: true,
	formFields,
	components,
});
